<template>
  <ion-page>
    <PageHeaderWithMenu title="Роли" :scrolled="headerScrolled" />
    <ion-content :class="[isMobile ? 'main-background' : 'liga-web-background']" :fullscreen="true" @ionScroll="onContentScroll">
      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Список ролей</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <ion-list v-if="allAuthorities.length > 0" class="roles-list">
            <ion-item v-for="a in allAuthorities" :key="a.name" class="role-item" lines="full">
              <ion-label>
                <h3>{{ a.name }}</h3>
                <p>{{ a.description || '—' }}</p>
                <p class="role-group">{{ groupLabel(a.groupId) }}</p>
                <p class="role-user-count">Пользователей: {{ userCount(a) }}</p>
              </ion-label>
              <ion-button slot="end" fill="clear" size="default" @click="openEdit(a)" :disabled="!canEdit(a)" title="Редактировать">
                <ion-icon :icon="pencilOutline"></ion-icon>
              </ion-button>
              <ion-button slot="end" fill="clear" size="default" color="danger" @click="confirmDelete(a)" :disabled="!canDelete(a)" title="Удалить">
                <ion-icon :icon="trashOutline"></ion-icon>
              </ion-button>
            </ion-item>
          </ion-list>
          <p v-else-if="!loadingRoles" class="ion-text-center ion-padding">Нет ролей</p>
          <ion-spinner v-if="loadingRoles" name="crescent" class="ion-padding"></ion-spinner>
          <ion-button fill="clear" size="small" @click="loadRoles()" :disabled="loadingRoles">Обновить</ion-button>
        </ion-card-content>
      </ion-card>

      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Добавить новую роль</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <ion-item>
            <ion-input label="Имя роли (ROLE_XXX)" label-placement="stacked" v-model="newRoleName"
              placeholder="ROLE_NEW"></ion-input>
          </ion-item>
          <ion-item>
            <ion-input label="Описание" label-placement="stacked" v-model="newRoleDescription"
              placeholder="Отображаемое название"></ion-input>
          </ion-item>
          <ion-note class="ion-padding-start ion-padding-end">Новые роли создаются только в группе «Допуск к программам».</ion-note>
          <ion-button expand="block" @click="createRole()" :disabled="!newRoleName.trim()">Создать роль</ion-button>
        </ion-card-content>
      </ion-card>

      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Кеш ролей пользователей</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <p class="ion-text-wrap">Обновить кеш ролей у всех пользователей (применяет изменения прав без повторного входа).</p>
          <ion-button expand="block" @click="refreshCache()">Обновить кеш ролей</ion-button>
        </ion-card-content>
      </ion-card>

      <ion-modal :is-open="!!editingRole" @didDismiss="closeEditModal" css-class="edit-role-modal">
        <ion-header class="edit-role-modal-header">
          <ion-toolbar>
            <ion-title>Редактировать роль</ion-title>
            <ion-buttons slot="end">
              <ion-button fill="clear" @click="closeEditModal">Отмена</ion-button>
              <ion-button @click="saveEdit()" :disabled="savingEdit">Сохранить</ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content class="edit-role-modal-content ion-padding" v-if="editingRole">
          <ion-item class="edit-role-item">
            <ion-label position="stacked">Имя роли</ion-label>
            <ion-input :value="editingRole.name" disabled></ion-input>
          </ion-item>
          <ion-item class="edit-role-item">
            <ion-input label="Описание" label-placement="stacked" v-model="editDescription" placeholder="Отображаемое название"></ion-input>
          </ion-item>
          <ion-item class="edit-role-item">
            <ion-select label="Группа" label-placement="stacked" v-model="editGroupId" interface="popover">
              <ion-select-option :value="1">Группы прав (общие)</ion-select-option>
              <ion-select-option :value="2">Допуск к программам</ion-select-option>
            </ion-select>
          </ion-item>
        </ion-content>
      </ion-modal>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { Authority } from '@/model/authority.model';
import { GlobalContext } from '@/model/globalcontext.model';
import AdminService from '@/services/admin.service';
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonNote,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTitle,
  IonToolbar,
  alertController,
  toastController,
} from '@ionic/vue';
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import { pencilOutline, trashOutline } from 'ionicons/icons';
import { defineComponent, inject } from 'vue';
import { useStore } from 'vuex';

let rolesStore: ReturnType<typeof useStore>;

export default defineComponent({
  name: 'AdminRoles',
  components: {
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonNote,
    IonPage,
    IonSelect,
    IonSelectOption,
    IonSpinner,
    IonTitle,
    IonToolbar,
    PageHeaderWithMenu,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    rolesStore = useStore();
    return {
      isMobile: globalContext.isMobile,
      pencilOutline,
      trashOutline,
    };
  },
  data() {
    return {
      adminService: new AdminService(),
      allAuthorities: [] as Authority[],
      loadingRoles: false,
      newRoleName: '',
      newRoleDescription: '',
      newRoleGroupId: 2,
      forbiddenRoles: ['ROLE_MANAGER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_SYSTEM'],
      editingRole: null as Authority | null,
      editDescription: '',
      editGroupId: 2 as number,
      savingEdit: false,
      headerScrolled: false,
    };
  },
  mounted() {
    this.loadRoles();
  },
  methods: {
    onContentScroll(e: CustomEvent) {
      const scrollTop = e.detail?.scrollTop ?? 0;
      this.headerScrolled = scrollTop > 16;
    },
    groupLabel(groupId: number | undefined): string {
      if (groupId == null) return '';
      if (groupId === 1) return 'Группы прав (общие)';
      if (groupId === 2) return 'Допуск к программам';
      return `Группа ${groupId}`;
    },
    userCount(a: Authority): number {
      return a.userCount != null ? a.userCount : 0;
    },
    canEdit(a: Authority): boolean {
      return !this.forbiddenRoles.includes(a.name);
    },
    canDelete(a: Authority): boolean {
      return !this.forbiddenRoles.includes(a.name);
    },
    closeEditModal() {
      this.editingRole = null;
    },
    openEdit(a: Authority) {
      this.editingRole = a;
      this.editDescription = a.description || '';
      this.editGroupId = a.groupId != null ? a.groupId : 2;
    },
    async saveEdit() {
      if (!this.editingRole) return;
      this.savingEdit = true;
      try {
        await this.adminService.updateAuthority(this.editingRole.name, {
          description: this.editDescription.trim() || undefined,
          groupId: this.editGroupId,
        });
        const toast = await toastController.create({
          message: 'Роль обновлена',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.$nextTick(() => {
          this.editingRole = null;
        });
        await this.loadRoles();
      } catch (e) {
        console.error('saveEdit', e);
        const toast = await toastController.create({
          message: 'Ошибка обновления роли',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.savingEdit = false;
      }
    },
    async confirmDelete(a: Authority) {
      const count = this.userCount(a);
      const alert = await alertController.create({
        header: 'Удалить роль?',
        message: `Роль «${a.name}» будет удалена у всех пользователей (сейчас: ${count}). Это действие нельзя отменить.`,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          { text: 'Удалить', role: 'destructive', handler: () => this.doDelete(a) },
        ],
      });
      await alert.present();
    },
    async doDelete(a: Authority) {
      try {
        await this.adminService.deleteAuthority(a.name);
        const toast = await toastController.create({
          message: 'Роль удалена, кеш ролей обновлён',
          duration: 2500,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        await this.loadRoles();
        rolesStore.commit('setRolesUpdated');
      } catch (e) {
        console.error('doDelete', e);
        const toast = await toastController.create({
          message: 'Ошибка удаления роли',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      }
    },
    async loadRoles() {
      this.loadingRoles = true;
      try {
        const list = await this.adminService.getAdminAuthorities();
        this.allAuthorities = (list || []).sort((a, b) =>
          (a.description || a.name).localeCompare(b.description || b.name));
      } catch (e) {
        console.error('loadRoles', e);
        const toast = await toastController.create({
          message: 'Ошибка загрузки списка ролей',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.loadingRoles = false;
      }
    },
    async createRole() {
      const name = this.newRoleName.trim();
      if (!name) return;
      
      // Формируем имя роли с префиксом ROLE_
      const finalName = name.startsWith('ROLE_') ? name.toUpperCase() : `ROLE_${name.toUpperCase()}`;
      
      // Проверяем, не является ли роль административной
      if (this.forbiddenRoles.includes(finalName)) {
        const toast = await toastController.create({
          message: `Создание административной роли ${finalName} запрещено из соображений безопасности`,
          duration: 4000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
        return;
      }
      
      try {
        await this.adminService.createAuthority({
          name: finalName,
          description: this.newRoleDescription.trim() || undefined,
          groupId: this.newRoleGroupId,
        });
        const toast = await toastController.create({
          message: 'Роль создана',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.newRoleName = '';
        this.newRoleDescription = '';
        await this.loadRoles();
        rolesStore.commit('setRolesUpdated');
      } catch (e) {
        console.error('createRole', e);
        const toast = await toastController.create({
          message: 'Ошибка создания роли',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      }
    },
    async refreshCache() {
      try {
        await this.adminService.refreshRolesCache();
        rolesStore.commit('setRolesUpdated');
        const toast = await toastController.create({
          message: 'Кеш ролей обновлён',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
      } catch (e) {
        console.error('refreshRolesCache', e);
        const toast = await toastController.create({
          message: 'Ошибка обновления кеша ролей',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      }
    },
  },
});
</script>

<style scoped>
.admin-card {
  margin: 16px;
  --background: var(--liga-float-glass-bg-strong);
}

.admin-card ion-card-title {
  color: var(--liga-chrome-text);
}

.admin-card ion-card-content {
  color: var(--liga-chrome-text);
}

.admin-card ion-item {
  --background: transparent;
  --color: var(--liga-chrome-text);
}

.header-title {
  letter-spacing: 0.08em;
}

.admin-card ion-note {
  color: var(--liga-chrome-text-muted);
  font-size: 0.9em;
  display: block;
  margin-bottom: 8px;
}

.roles-list {
  background: transparent;
}

.admin-card .role-item {
  --background: transparent;
  --color: var(--liga-chrome-text);
  --border-color: var(--liga-surface-border);
}

.admin-card .role-item ion-label h3 {
  color: var(--liga-chrome-text);
  font-weight: 600;
}

.admin-card .role-item ion-label p {
  color: var(--liga-chrome-text-muted);
  font-size: 0.9em;
}

.admin-card .role-group {
  color: var(--liga-chrome-text-muted) !important;
  font-size: 0.85em !important;
}

.admin-card .role-user-count {
  color: var(--liga-chrome-text-muted) !important;
  font-size: 0.85em !important;
  margin-top: 2px;
}

/* Кнопки в стиле приложения: чёрно-оранжевый */
.admin-card ion-button:not([fill="clear"]) {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --color: var(--liga-chrome-text);
  --color-activated: white;
  font-weight: bold;
  --border-radius: 14px;
  margin-top: 8px;
}

.admin-card ion-button[fill="clear"] {
  --color: var(--ion-color-orange);
  --color-activated: var(--ion-color-orange-tint);
  font-weight: 600;
}

</style>

<style lang="css">
/* Модалка редактирования роли — без scoped, т.к. контент в портале; цветовая гамма приложения */
.edit-role-modal .edit-role-modal-header ion-toolbar {
  --background: var(--liga-float-glass-bg-strong);
  --border-color: var(--liga-surface-border);
}

.edit-role-modal .edit-role-modal-header ion-title {
  color: var(--liga-chrome-text);
  font-weight: 600;
}

.edit-role-modal .edit-role-modal-header ion-button[fill="clear"] {
  --color: var(--ion-color-orange);
  --color-activated: var(--ion-color-orange-tint);
  font-weight: 600;
}

.edit-role-modal .edit-role-modal-header ion-button:not([fill="clear"]) {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --color: var(--liga-chrome-text);
  font-weight: bold;
  --border-radius: 14px;
}

.edit-role-modal .edit-role-modal-content {
  --background: var(--liga-float-glass-bg-strong);
  --color: var(--liga-chrome-text);
}

.edit-role-modal .edit-role-item {
  --background: transparent;
  --color: var(--liga-chrome-text);
  --border-color: var(--liga-surface-border);
}

.edit-role-modal .edit-role-item ion-label {
  color: var(--liga-chrome-text);
}

.edit-role-modal .edit-role-item ion-input,
.edit-role-modal .edit-role-item ion-select {
  --color: var(--liga-chrome-text);
  --placeholder-color: var(--liga-chrome-text-muted);
}
</style>
